import type { TimeRange} from "@@/shared/date-time/time-range";
import { timeRangeZodSchema } from "@@/shared/date-time/time-range";
import type { UnixTimestamp } from "@@/shared/date-time/unix";
import type { OrderItem_V1 } from "@@/shared/orders/order-items/order-item-v1";
import type { OrderItem_V2 } from "@@/shared/orders/order-items/order-item-v2";
import { emptyArrayOf } from "@@/utilities/empty";
import { z } from "zod";
import type { DiscountCondition } from "./discount-condition";
import type {
    DiscountConditionId} from "./discount-condition-id";
import {
    createDiscountConditionId
} from "./discount-condition-id";
import type { DiscountConditionValidator } from "./discount-condition-validator";

const timeRangeConditionType = "TIME_RANGE_CONDITION";
type TimeRangeConditionType = typeof timeRangeConditionType;

type TimeRangeCondition = {
    _id: DiscountConditionId;
    type: TimeRangeConditionType;
    timeRange: TimeRange;
};

const timeRangeConditionFactory = (
    timeRange: TimeRange,
): TimeRangeCondition => {
    return {
        _id: createDiscountConditionId(),
        type: "TIME_RANGE_CONDITION",
        timeRange,
    };
};

const getTimeRangeConditionValidator =
    <TOrderItem extends OrderItem_V1 | OrderItem_V2 = OrderItem_V2>(
        condition: TimeRangeCondition,
    ): DiscountConditionValidator<
        TOrderItem,
        { at: { unix: UnixTimestamp } }
    > =>
    (items: TOrderItem[], params: { at: { unix: UnixTimestamp } }) => {
        const { at } = params;
        return at.unix >= condition.timeRange.start.unix &&
            at.unix <= condition.timeRange.end.unix
            ? items
            : emptyArrayOf<TOrderItem>();
    };

const timeRangeConditionZodSchema: z.ZodSchema<TimeRangeCondition> = z.object({
    _id: z.string() as unknown as z.ZodSchema<DiscountConditionId>,
    type: z.literal("TIME_RANGE_CONDITION"),
    timeRange: timeRangeZodSchema,
});

const isTimeRangeCondition = (
    condition: DiscountCondition,
): condition is TimeRangeCondition => {
    return condition.type === timeRangeConditionType;
};

export {
    getTimeRangeConditionValidator,
    isTimeRangeCondition,
    timeRangeConditionFactory,
    timeRangeConditionType,
    timeRangeConditionZodSchema,
};
export type { TimeRangeCondition, TimeRangeConditionType };
